import { ethers } from "ethers";
import Parse from "parse";
import PubSub from "pubsub-js";

import * as ClaimTopicsRegistry from "../abi/IClaimTopicsRegistry.json";
import * as IdentityFactory from "../abi/IdentityFactory.json";
import * as IdentityRegistry from "../abi/IIdentityRegistry.json";
import * as TrustedIssuersRegistry from "../abi/ITrustedIssuersRegistry.json";
import ParseClient from "../services/ParseClient"; // Import the singleton instance
import { NomyxEvent } from "../utils/Constants";

class BlockchainService {
  claimTopicsAbi = ClaimTopicsRegistry.default.abi;
  identityRegistryAbi = IdentityRegistry.default.abi;
  trustedIssuersRegistryAbi = TrustedIssuersRegistry.default.abi;
  identityFactoryAbi = IdentityFactory.default.abi;

  constructor(provider, contractAddress, identityRegistryAddress) {
    this.contractAddress = contractAddress;
    this.provider = provider;

    // The contract.on(...) listeners below poll this provider for logs. It used to be
    // created with `_pollingInterval = Infinity`, which setInterval treats as ~0, so the
    // listeners hammered eth_getLogs; 30s matches App.tsx's setupProvider(). Passing the
    // configured chain id means no eth_chainId detection at startup, which ethers v6 would
    // otherwise retry (and log) every second for as long as the RPC is unreachable.
    const chainId = Number(process.env.REACT_APP_HARDHAT_CHAIN_ID);
    this.dedicatedProvider = new ethers.JsonRpcProvider(process.env.REACT_APP_RPC_URL, chainId ? { name: "unknown", chainId } : undefined, {
      staticNetwork: true,
      pollingInterval: 30000,
    });
    console.log("blockchain service provider: ", this.provider);

    const contractProvider = this.dedicatedProvider;

    // ✅ Initialize smart contracts
    this.claimTopicRegistryService = new ethers.Contract(contractAddress, this.claimTopicsAbi, contractProvider);
    this.identityRegistryService = new ethers.Contract(contractAddress, this.identityRegistryAbi, contractProvider);
    this.trustedIssuersRegistryService = new ethers.Contract(contractAddress, this.trustedIssuersRegistryAbi, contractProvider);
    this.identityFactoryService = new ethers.Contract(identityRegistryAddress, this.identityFactoryAbi, contractProvider);

    // Claim Topics Registry
    this.getClaimTopics = this.getClaimTopics.bind(this);

    // Identity Registry
    this.getDigitalIdentity = this.getDigitalIdentity.bind(this);
    this.getActiveIdentities = this.getActiveIdentities.bind(this);
    this.getPendingIdentities = this.getPendingIdentities.bind(this);
    this.updateIdentity = this.updateIdentity.bind(this);
    this.createParseIdentity = this.createParseIdentity.bind(this);
    this.softRemoveUser = this.softRemoveUser.bind(this);
    this.approveUser = this.approveUser.bind(this);
    this.contains = this.contains.bind(this);
    this.isVerified = this.isVerified.bind(this);
    this.identity = this.identity.bind(this);
    this.getRegistryUsers = this.getRegistryUsers.bind(this);
    this.isRegistryUser = this.isRegistryUser.bind(this);
    this.getClaims = this.getClaims.bind(this);
    this.getClaim = this.getClaim.bind(this);
    this.hasClaim = this.hasClaim.bind(this);
    this.getTrustedIssuersByObjectId = this.getTrustedIssuersByObjectId.bind(this);

    // Trusted Issuers Registry
    this.getTrustedIssuers = this.getTrustedIssuers.bind(this);
    this.isTrustedIssuer = this.isTrustedIssuer.bind(this);
    this.getTrustedIssuerClaimTopics = this.getTrustedIssuerClaimTopics.bind(this);
    this.hasClaimTopic = this.hasClaimTopic.bind(this);

    this.getTokenProjects = this.getTokenProjects.bind(this);

    // ethers 6 returns a promise from .on(); a failed subscription still surfaces as an unhandled rejection.
    void this.claimTopicRegistryService.on(NomyxEvent.ClaimTopicAdded, (claimTopic) => PubSub.publish(NomyxEvent.ClaimTopicAdded, claimTopic));
    void this.claimTopicRegistryService.on(NomyxEvent.ClaimTopicRemoved, (claimTopic) => PubSub.publish(NomyxEvent.ClaimTopicRemoved, claimTopic));
    void this.trustedIssuersRegistryService.on(NomyxEvent.ClaimTopicsUpdated, (trustedIssuer, claimTopics) =>
      PubSub.publish(NomyxEvent.ClaimTopicsUpdated, {
        trustedIssuer,
        claimTopics,
      })
    );
    void this.trustedIssuersRegistryService.on(NomyxEvent.TrustedIssuerAdded, (trustedIssuer, claimTopics) =>
      PubSub.publish(NomyxEvent.TrustedIssuerAdded, {
        trustedIssuer,
        claimTopics,
      })
    );
    void this.trustedIssuersRegistryService.on(NomyxEvent.TrustedIssuerRemoved, (trustedIssuer) =>
      PubSub.publish(NomyxEvent.TrustedIssuerRemoved, trustedIssuer)
    );
    void this.identityRegistryService.on(NomyxEvent.ClaimAdded, (claimId, topic, scheme, issuer, signature, data, uri) =>
      PubSub.publish(NomyxEvent.ClaimAdded, {
        claimId,
        topic,
        scheme,
        issuer,
        signature,
        data,
        uri,
      })
    );
    void this.identityRegistryService.on(NomyxEvent.ClaimRemoved, (claimId, topic, scheme, issuer, signature, data, uri) =>
      PubSub.publish(NomyxEvent.ClaimRemoved, {
        claimId,
        topic,
        scheme,
        issuer,
        signature,
        data,
        uri,
      })
    );
    void this.identityRegistryService.on(NomyxEvent.IdentityAdded, (address, identity) =>
      PubSub.publish(NomyxEvent.IdentityAdded, { address, identity })
    );
    void this.identityRegistryService.on(NomyxEvent.IdentityRemoved, (address, identity) =>
      PubSub.publish(NomyxEvent.IdentityRemoved, { address, identity })
    );
    void this.identityRegistryService.on(NomyxEvent.IdentityCountryUpdated, (identity, country) =>
      PubSub.publish(NomyxEvent.IdentityCountryUpdated, { identity, country })
    );
    void this.identityRegistryService.on(NomyxEvent.ClaimAdded, (identity, claimTopic, claim) =>
      PubSub.publish(NomyxEvent.ClaimAdded, { identity, claimTopic, claim })
    );
    void this.identityRegistryService.on(NomyxEvent.ClaimRemoved, (identity, claimTopic) =>
      PubSub.publish(NomyxEvent.ClaimRemoved, { identity, claimTopic })
    );
  }

  // Event listeners
  publish(event, data) {
    PubSub.publish(event, data);
  }

  subscribe(event, handler) {
    return PubSub.subscribe(event, handler);
  }

  unsubscribe(token) {
    return PubSub.unsubscribe(token);
  }

  async updateClaimTopic(claimTopic) {
    // Runs server-side (master key) via the updateClaimTopic cloud function instead of
    // writing to the Public-Read+Write ClaimTopic class directly from the browser.
    return await Parse.Cloud.run("updateClaimTopic", {
      topic: claimTopic.topic,
      displayName: claimTopic.displayName,
    });
  }

  async getClaimTopics() {
    try {
      // notEqualTo (not equalTo("active", true)) so rows saved before the
      // `active` field existed still show up.
      // .each() paginates internally instead of relying on a fixed limit; it
      // doesn't allow sort/limit on the query itself, so sort after collecting.
      const query = new Parse.Query("ClaimTopic");
      query.notEqualTo("active", false);
      const results = [];
      await query.each((obj) => results.push(obj));
      results.sort((a, b) => a.get("createdAt") - b.get("createdAt"));
      return results;
    } catch (e) {
      console.log(`getClaimTopics: Error getting records: ${e}`);
      return [];
    }
  }

  async getNextClaimTopicId() {
    const result = await ParseClient.getRecords("ClaimTopic", [], [], ["topic"], 1, 0, "createdAt", "desc");
    let highestTopicId = result.length > 0 ? Number.parseInt(result[0].attributes.topic) + 1 : 1;
    return highestTopicId;
  }

  async getClaimTopicById(id) {
    const claimTopic = await ParseClient.getRecords("ClaimTopic", ["objectId"], [id], ["*"]);
    return claimTopic;
  }

  async getTrustedIssuersForClaimTopics(id) {
    const trustedIssuer = await ParseClient.getRecords("TrustedIssuer", ["claimTopics.topic"], [id]);
    return trustedIssuer;
  }

  async getTrustedIssuersByObjectId(id) {
    const results = await ParseClient.getRecords("TrustedIssuer", ["objectId"], [id]);
    return results.length > 0 ? results[0] : null;
  }

  /**
   * Retrieves claims associated with a specific claim topic
   * @param {string} id - The object ID of the claim topic
   * @returns {Array} - Array of claims or empty array if none found
   */
  async getClaimsForClaimTopics(id) {
    try {
      // Validate input
      if (!id) {
        return [];
      }

      // Get the claim topic details
      const claimTopicObj = await ParseClient.getRecords("ClaimTopic", ["objectId"], [id]);
      if (!claimTopicObj || claimTopicObj.length === 0) {
        return [];
      }

      // Extract the topic value
      const claimTopicValue = claimTopicObj[0].attributes.topic;

      // Get claims using the topic value directly
      const claimTopics = await ParseClient.getRecords("Claim", ["topic"], [claimTopicValue], ["*"]);

      // Ensure we have valid claim data
      if (!claimTopics || !Array.isArray(claimTopics)) {
        return [];
      }

      // Re-implement identity filtering with proper error handling
      try {
        // Get identities that contain this claim topic
        const identities = await ParseClient.getRecords("Identity", ["claims"], [claimTopicValue]);

        // Only filter if we have valid identities data
        if (identities && Array.isArray(identities) && identities.length > 0) {
          return claimTopics.filter(
            (claim) => claim?.attributes?.identity && identities.some((identity) => identity?.attributes?.identity === claim.attributes.identity)
          );
        }
      } catch (filterError) {
        // If filtering fails, fall back to returning all claims
      }

      // Return all claims if filtering is not possible
      return claimTopics;
    } catch (error) {
      // Log error but don't expose details to client
      console.error("Error in getClaimsForClaimTopics:", error);
      return [];
    }
  }

  async getActiveIdentities() {
    try {
      const identities = await ParseClient.getRecords("Identity", ["active"], [true], ["*"]);

      if (identities && identities.length > 0) {
        const claims = await ParseClient.getRecords("Claim", ["active"], [true], ["identityObj", "claimTopicObj"]);

        if (claims && claims.length > 0) {
          for (const identity of identities) {
            const walletAddress = identity.get("walletAddress");

            if (walletAddress) {
              const userRecords = await ParseClient.getRecords("User", ["walletAddress"], [walletAddress], ["pepMatched", "watchlistMatched"]);
              if (userRecords && userRecords.length > 0) {
                const user = userRecords[0]; // Assuming walletAddress is unique and returns one record
                identity.pepMatched = user.attributes?.pepMatched || false;
                identity.watchlistMatched = user.attributes?.watchlistMatched || false;
                identity.personaVerificationData = user.attributes?.personaVerificationData || null;
                if (user.attributes?.username) {
                  identity.email = user.attributes.username;
                }
              }
            }

            // Claims for this identity
            const activeClaims = claims.filter((claim) => {
              const identityObj = claim.get("identityObj");
              return identityObj && identityObj.id === identity.id;
            });

            // Sort them by topic
            const sortedClaims = activeClaims.sort((a, b) => {
              const topicA = a.attributes?.claimTopicObj?.attributes?.topic || "";
              const topicB = b.attributes?.claimTopicObj?.attributes?.topic || "";
              return topicA > topicB ? 1 : -1;
            });

            // Store in _claims for safe frontend access
            identity._claims = sortedClaims;
          }
        }
      }

      return identities;
    } catch (error) {
      console.error("Error fetching active identities or claims:", error);
      throw error;
    }
  }

  async getPendingIdentities() {
    const users = await ParseClient.getRecords("_User", ["pendingApproval", "denied"], [true, false], ["*"]);
    return users;
  }

  async getAddClaimIdentities() {
    const identities = await ParseClient.getRecords("Identity", ["active"], [true], ["*"]);
    if (identities && identities.length > 0) {
      const claims = await ParseClient.getRecords("Claim", ["active"], [true], ["identityObj", "claimTopicObj"]);

      for (let i = 0; i < identities.length; i++) {
        const identity = identities[i];

        // Filter and map claims to the corresponding identity
        const activeClaims = claims.filter((claim) => claim.get("identityObj").id === identity.id);
        identity.attributes.claims = { children: activeClaims };

        // Sort the claims by topic
        identity.attributes.claims.children.sort((a, b) => {
          return a.attributes.claimTopicObj.attributes.topic > b.attributes.claimTopicObj.attributes.topic ? 1 : -1;
        });
      }

      // Filter identities to only include those without claims
      const identitiesWithoutClaims = identities.filter((identity) => !identity.attributes.claims.children.length);

      return identitiesWithoutClaims;
    }

    return [];
  }

  async softRemoveUser(identityAddress) {
    // set pending approval and denied to false
    return await ParseClient.updateExistingRecord("_User", ["walletAddress"], [identityAddress], { denied: true });
  }

  async updateIdentity(identity, identityData) {
    const maxRetries = 3,
      baseDelay = 4000;

    if (!identityData) {
      console.warn("No identity data provided, skipping update.");
      return null;
    }

    // Retry helper function
    async function retryWithBackoff(fn, retries, delay) {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const result = await fn();
          if (result) return result; // Return if successful
        } catch (error) {
          console.error(`Attempt ${attempt} failed:`, error);
        }

        if (attempt < retries) {
          const waitTime = delay * attempt; // Exponential backoff
          console.log(`Retrying in ${waitTime / 1000} seconds...`);
          await new Promise((resolve) => setTimeout(resolve, waitTime));
        }
      }
      console.error("Update failed after max retry attempts.");
      return null;
    }

    // Perform the update with retry
    return retryWithBackoff(() => ParseClient.updateExistingRecord("Identity", ["walletAddress"], [identity], identityData), maxRetries, baseDelay);
  }

  async createParseIdentity(identity, identityData) {
    return await ParseClient.createRecord("_User", ["address"], [identity], identityData);
  }

  async isUser(identityAddress) {
    const user = await ParseClient.getRecord("_User", ["walletAddress"], [identityAddress]);
    return user ? true : false;
  }

  async approveUser(identityAddress) {
    return await ParseClient.updateExistingRecord("_User", ["walletAddress"], [identityAddress], { pendingApproval: false });
  }

  // "getIdentityWithClaimTopics" and "getRemovedIdentityDetails" are now deployed to
  // kpmg's backend (src/cloud-functions/identity.ts), so this is back to the full
  // single-round-trip version — active claims + Active Identity card come from
  // getIdentityWithClaimTopics, removed claims from the separate getIdentityClaimHistory
  // (which replays the raw ClaimAdded__e/ClaimRemoved__e event tables).
  async getDigitalIdentity(id) {
    const result = await Parse.Cloud.run("getIdentityWithClaimTopics", { id });
    if (!result) return null;

    // Removed-claim history comes from the separate "getIdentityClaimHistory" cloud
    // function — getIdentityWithClaimTopics above only ever tracks the active set.
    if (result.identity) {
      try {
        const { claimHistory } = await ParseClient.run("getIdentityClaimHistory", { identity: result.identity });
        result.removedClaims = (claimHistory || []).filter((claim) => !claim.active);
      } catch (err) {
        console.error("Failed to fetch claim history:", err);
        result.removedClaims = [];
      }
    }

    return result;
  }

  async getRemovedIdentityDetails(id) {
    // Backs the Removed Identity card. The Removed tab's "View" passes the _User
    // objectId, so this resolves _User -> wallet -> (inactive) Identity -> ONCHAINID
    // -> IdentityRemoved__e and returns the trusted issuer who removed the identity
    // (wallet + email), the transaction hash and the removal date. Returns null when
    // there is no removal event for this user.
    return await Parse.Cloud.run("getRemovedIdentityDetails", { id });
  }

  async contains(userAddress) {
    return await this.identityRegistryService.contains(userAddress);
  }

  async isVerified(userAddress) {
    try {
      if (!this.dedicatedProvider) {
        throw new Error("No dedicated provider available for isVerified()");
      }

      const contract = new ethers.Contract(this.identityRegistryService.target, this.identityRegistryAbi, this.dedicatedProvider);

      console.log("Using dedicated provider for isVerified:", this.dedicatedProvider._getConnection().url);

      return await contract.isVerified(userAddress);
    } catch (err) {
      console.error("❌ Error in isVerified():", err);
      throw err;
    }
  }

  async identity(userAddress) {
    return await this.identityRegistryService.identity(userAddress);
  }

  async getRegistryUsers() {
    return await this.identityRegistryService.getRegistryUsers();
  }

  async isRegistryUser(registryUser) {
    return await this.identityRegistryService.isRegistryUser(registryUser);
  }

  async getClaims(registryUser) {
    return await this.identityRegistryService.getClaims(registryUser);
  }

  async getClaim(registryUser, claimTopic) {
    return this.identityRegistryService && (await this.identityRegistryService.getClaim(registryUser, claimTopic));
  }

  async hasClaim(registryUser, claimTopic) {
    return await this.identityRegistryService.hasClaim(registryUser, claimTopic);
  }

  // TrustedIssuer is CLP-locked to master (src/auth/policy.ts), so this can no
  // longer write the class directly
  async updateTrustedIssuer(data) {
    if (!data || !data.issuer) {
      console.warn("Invalid data provided, skipping update.");
      return null;
    }

    const normalizedIssuer = data.issuer.toLowerCase();
    try {
      const result = await Parse.Cloud.run("updateTrustedIssuer", { ...data, issuer: normalizedIssuer });
      return result;
    } catch (error) {
      console.error(`updateTrustedIssuer failed for issuer '${normalizedIssuer}':`, error);
      return null;
    }
  }

  async getTrustedIssuers() {
    const trustedIssuer = await ParseClient.getRecords("TrustedIssuer", ["active"], [true], ["*"]);
    return trustedIssuer;
  }

  //temporary fix: the idea is to update this service (and App) to use dedicated provider for getters
  async isTrustedIssuer(issuer) {
    if (!this.contractAddress) {
      console.error("contractAddress is undefined in isTrustedIssuer");
      throw new Error("Missing contract address for trusted issuer check");
    }

    if (!this.trustedIssuersRegistryAbi) {
      console.error("trustedIssuersRegistryAbi is undefined");
      throw new Error("Missing ABI for trusted issuer contract");
    }

    if (!this.dedicatedProvider) {
      console.error("dedicatedProvider is undefined");
      throw new Error("Missing dedicated provider for read-only contract call");
    }

    try {
      const contract = new ethers.Contract(this.contractAddress, this.trustedIssuersRegistryAbi, this.dedicatedProvider);
      const result = await contract.isTrustedIssuer(issuer);
      console.log("isTrustedIssuer result:", result);
      return result;
    } catch (err) {
      console.error("Error calling isTrustedIssuer:", err);
      throw err;
    }
  }

  async getTrustedIssuerClaimTopics(trustedIssuer) {
    return await this.trustedIssuersRegistryService.getTrustedIssuerClaimTopics(trustedIssuer);
  }

  async hasClaimTopic(issuer, claimTopic) {
    return await this.trustedIssuersRegistryService.hasClaimTopic(issuer, claimTopic);
  }

  async getTokenProjects() {
    try {
      const TokenProject = Parse.Object.extend("TokenProject");
      const query = new Parse.Query(TokenProject);

      // query.equalTo("status", "active");

      // Sort by creation date (newest first)
      query.descending("createdAt");

      // Limit results if needed
      query.limit(100);

      const results = await query.find();

      return results.map((project) => ({
        id: project.id,
        title: project.get("title"),
        description: project.get("description"),
        // Add other fields
        tradeDeadId: project.get("tradeDeadId"),
        interestRate: project.get("interestRate"),
        industryTemplate: project.get("industryTemplate"),
        logo: project.get("logo"),
        updatedAt: project.get("updatedAt"),
        createdAt: project.get("createdAt"),
        fields: project.get("fields"),
        projectInfo: project.get("projectInfo"),
      }));
    } catch (error) {
      console.error("Error fetching token projects:", error);
      throw error;
    }
  }

  async getClaimTopicByTopic(topic) {
    try {
      if (topic === null || topic === undefined || topic === "") {
        console.warn("getClaimTopicByTopic called with empty topic");
        return null;
      }

      const records = await ParseClient.getRecords("ClaimTopic", ["topic"], [String(topic)], ["*"]);

      return records && records.length > 0 ? records[0] : null;
    } catch (error) {
      console.error(`Error fetching ClaimTopic with topic ${topic}:`, error);
      throw error;
    }
  }

  async getTrustedIssuerByAddress(walletAddress) {
    try {
      if (!walletAddress || typeof walletAddress !== "string") {
        console.warn("getTrustedIssuerByAddress called with invalid wallet address");
        return null;
      }

      const normalizedAddress = walletAddress.toLowerCase();

      const records = await ParseClient.getRecords("TrustedIssuer", ["issuer"], [normalizedAddress], ["*"]);

      return records && records.length > 0 ? records[0] : null;
    } catch (error) {
      console.error(`Error fetching TrustedIssuer with address ${walletAddress}:`, error);
      throw error;
    }
  }
}

export default BlockchainService;
